import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, X, User } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { getAuthHeaders } from '../lib/auth';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationPanel = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    loadNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Close panel when clicking outside
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: getAuthHeaders()
      });
      const newNotifications = response.data.notifications || [];
      
      // Check if there are new notifications and play sound
      if (newNotifications.length > notifications.length) {
        playNotificationSound();
        showBrowserNotification(newNotifications[0]);
      }
      
      setNotifications(newNotifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const playNotificationSound = () => {
    // Sound disabled per user request
    // try {
    //   const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBDGH0fPTgjMGHm7A7+OZSAKJQJ/j8LZkHAU7k9ryzX0vBSl+zPHejUsOFmm98+qcTwwQ');
    //   audio.play().catch(() => {});
    // } catch (error) {
    //   console.error('Failed to play sound:', error);
    // }
  };

  const showBrowserNotification = async (notification) => {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      return;
    }

    // Request permission if needed
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return;
      }
    }

    // Don't show if permission is denied
    if (Notification.permission !== 'granted') {
      return;
    }

    try {
      // Check if service worker is available and use it
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification('New Notification', {
          body: notification.message,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: notification.id,
          requireInteraction: false
        });
      } else {
        // Fallback to regular Notification API if no service worker
        new Notification('New Notification', {
          body: notification.message,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: notification.id,
          requireInteraction: false
        });
      }
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    // Navigate to relevant page based on notification type
    if (notification.type === 'service_match' && notification.fromUserId) {
      navigate(`/profile/${notification.fromUserId}`);
    } else if (notification.type === 'contact_download' && notification.downloaderId) {
      navigate(`/profile/${notification.downloaderId}`);
    } else if (notification.type === 'website_ready' && notification.websiteId) {
      navigate(`/auto-site/${notification.websiteId}`);
    } else if (notification.type === 'contact_saved' && notification.fromUserId) {
      navigate(`/profile/${notification.fromUserId}`);
    } else if (notification.fromUserId) {
      // Fallback: navigate to the profile of whoever triggered the notification
      navigate(`/profile/${notification.fromUserId}`);
    }
    setIsOpen(false);
  };

  const unreadCount = notifications.length;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-slate-200 z-50">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <p className="text-slate-600">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No new notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification, idx) => (
                  <div
                    key={notification.id || idx}
                    onClick={() => handleNotificationClick(notification)}
                    className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`notification-item-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 font-medium mb-1">
                          New Lead Opportunity! 🎯
                        </p>
                        <p className="text-sm text-slate-700 mb-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-slate-500">
                          {notification.createdAt ? dayjs(notification.createdAt).fromNow() : 'just now'}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-slate-200 text-center">
              <button
                onClick={() => {
                  notifications.forEach(n => markAsRead(n.id));
                  toast.success('All notifications marked as read');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
